// VideoWatermark.jsx
// Leaker detection component
// If user tries to modify watermark, app would crash.
// If `props.isSuspended` is `ture`, crashing will be suspended until it turns to `false`.

import React from 'react';
import PropTypes from 'prop-types';
import getPlayerRealSize from '../resource/getPlayerRealSize.js';
import trackMixpanel, { EventTypes, getViewId } from '../resource/mixpanel.js';
import { getItem, setItem } from '../resource/persist.js';
import { withScope } from '@sentry/browser';

/**
 * @typedef VideoBounds
 * @property {number} realWidth
 * @property {number} realHeight
 * @property {number} minLeft
 * @property {number} maxLeft
 * @property {number} minTop
 * @property {number} maxTop
 */

export class VideoWatermark extends React.PureComponent {
  state = {
    watermarkKey: '',
    watermarkTop: 5,
    watermarkLeft: 5,
  };

  /** @type {VideoBounds?} */
  videoBounds = null;

  /** @type {React.RefObject<HTMLElement>} */
  watermarkElement = React.createRef();

  isWindowSizeDirty = true;

  isTriggerPostponed = false;
  postponedTrackArgs = null;
  triggerTimeoutHandle = null;

  /** @type {[number]?} */
  trackedValidationPixelCounts = null;

  lastUpdateWatermarkTime = 0;

  componentDidMount() {
    window.addEventListener('resize', this.handleWindowResize);
    this.getTrackedValidationMeta();
  }

  componentDidUpdate(prevProps) {
    const { startWatermarkFlag, player } = this.props;

    if (
      (startWatermarkFlag !== prevProps.startWatermarkFlag && // startWatermarkFlag just set
        startWatermarkFlag &&
        player) ||
      ((!prevProps.player || prevProps.player !== player) && // player just set
        player &&
        startWatermarkFlag)
    ) {
      player.on('timeupdate', this.handleTimeUpdate);
    } else if (prevProps.player && player !== prevProps.player) {
      // player instance changed or unsetted
      prevProps.player.off?.('timeupdate', this.handleTimeUpdate);
    }

    if (
      prevProps.isSuspended &&
      !this.props.isSuspended &&
      this.isTriggerPostponed
    ) {
      this.trigger();
    }
  }
  componentWillUnmount() {
    this.props.player?.off?.('timeupdate', this.handleTimeUpdate);
    window.removeEventListener('resize', this.handleWindowResize);
    clearTimeout(this.triggerTimeoutHandle);
  }

  handleTimeUpdate = () => {
    const now = Date.now();
    if (now - this.lastUpdateWatermarkTime >= 5000) {
      this.lastUpdateWatermarkTime = now;
      this.updateWatermark();
    }
  };

  handleWindowResize = () => {
    this.isWindowSizeDirty = true;
  };

  getTrackedValidationMeta = async () => {
    this.trackedValidationPixelCounts = (await getItem('_twvpc')) || [];
  };

  /** Track user's leaker behavior */
  track = ({ changedStyleList }) => {
    trackMixpanel({
      type: EventTypes.USER_WATERMARK_STYLE_CHANGED,
      payload: {
        viewId: getViewId({
          pathname: window.location.pathname,
        }),
        changedStyleList,
        messageId: this.props.messageId,
        userUsername: this.props.userUsername,
      },
    });
  };

  /** Track number of pixels filled and computedStyle.font in watermark validation */
  trackWatermarkValidation = ({
    filledPixelCount,
    computedStyleFont,
    error,
  }) => {
    trackMixpanel({
      type: EventTypes.USER_WATERMARK_VALIDATE,
      payload: {
        viewId: getViewId({
          pathname: window.location.pathname,
        }),
        messageId: this.props.messageId,
        userUsername: this.props.userUsername,
        filledPixelCount,
        computedStyleFont,
        error,
      },
    });
  };

  /** Crash app in random seconds */
  trigger = () => {
    // In case of user was tracked when logged-out
    if (this.isTriggerPostponed) {
      this.track(this.postponedTrackArgs);
    }
    this.triggerTimeoutHandle = setTimeout(
      () => {
        // Crash React in random callstack
        React.PureComponent.prototype.componentDidUpdate = () => {
          withScope(scope => {
            scope.setFingerprint(['leaker-change-style']);
            throw new Error('leaker change style');
          });
        };
      },
      (Math.random() * 5 + 5) * 1000
    );
  };

  /**
   * Calculate styles to be set on element explicitly
   */
  getStyle = () => ({
    color: 'rgb(255, 255, 255)',
    opacity: '0.3',
    position: 'absolute',
    top: `${this.state.watermarkTop}px`,
    left: `${this.state.watermarkLeft}px`,
    fontSize: '16px',
  });

  /**
   * @type {CSSStyleDeclaration}
   */
  cssValidation = {
    // The following are from `getStyle()`
    position: ['absolute'],
    // The following are inherited
    display: ['block'],
    visibility: ['visible'],
    transform: ['none'],
    filter: ['none'],
    margin: ['0px', ''],
    padding: ['0px', ''],
    paddingInlineStart: ['0px', undefined],
    paddingInlineEnd: ['0px', undefined],
    paddingBlockStart: ['0px', undefined],
    paddingBlockEnd: ['0px', undefined],
    perspective: ['none'],
    float: ['none'],
    grid: [
      'none / none / none / row / auto / auto',
      'none / none / none / row / auto / auto / 0px / 0px',
      '',
    ],
    gridArea: ['auto / auto / auto / auto', 'auto', ''],
  };

  validateWatermark = () => {
    const { username } = this.props;
    const watermarkElement = this.watermarkElement.current;
    const computedStyle = window.getComputedStyle(watermarkElement);

    const changedStyleList = [];

    if (watermarkElement.innerHTML !== username) {
      changedStyleList.push(`element.innerHTML:${watermarkElement.innerHTML}`);
    }

    this.validateFontWithCanvas(computedStyle);
    this.validateWithClonedElement(computedStyle, changedStyleList);
    if (!this.isWindowSizeDirty) {
      this.validateWithBounds(
        this.videoBounds,
        this.watermarkElement.current,
        this.props.player
      );
    }

    // Validate styles by `cssValidation`
    for (const key in this.cssValidation) {
      const value = computedStyle[key];
      const validation = this.cssValidation[key];

      if (validation.indexOf(value) === -1) {
        changedStyleList.push(`${key}:${value}`);
      }
    }

    const {
      opacity,
      top: cssTop,
      left: cssLeft,
      zIndex,
      color,
      fontSize,
    } = computedStyle;

    // 0 or '' is ok
    if (zIndex !== 'auto' && zIndex != 0) {
      changedStyleList.push(`zIndex:${zIndex}`);
    }

    if (Number(opacity) < 0.3) {
      changedStyleList.push(`opacity:${Number(opacity)}`);
    }

    if (this.videoBounds) {
      if (
        !cssTop.includes('px') ||
        parseFloat(cssTop) < this.videoBounds.minTop ||
        parseFloat(cssTop) > this.videoBounds.maxTop
      ) {
        changedStyleList.push(`top:${cssTop}`);
      }

      if (
        !cssLeft.includes('px') ||
        parseFloat(cssLeft) < this.videoBounds.minLeft ||
        parseFloat(cssLeft) > this.videoBounds.maxLeft
      ) {
        changedStyleList.push(`left:${cssLeft}`);
      }
    }

    if (color.includes('rgb')) {
      if (color.includes('rgba')) {
        const alpha = color.replace(/\s|rgb\(|rgba\(|\)/g, '').split(',')[3];
        if (Number(alpha) < 1) {
          changedStyleList.push(`color:${color}`);
        }
      }
    } else {
      changedStyleList.push(`color:${color}`);
    }

    if (!fontSize.includes('px') || parseFloat(fontSize) <= 5) {
      changedStyleList.push(`fontSize:${fontSize}`);
    }

    if (changedStyleList.length !== 0) {
      const dedupedChangedStyleList = Array.from(new Set(changedStyleList));
      const trackArgs = {
        changedStyleList: dedupedChangedStyleList,
      };
      this.track(trackArgs);
      const { isSuspended } = this.props;
      if (isSuspended) {
        this.isTriggerPostponed = true;
        this.postponedTrackArgs = trackArgs;
      } else {
        this.trigger();
      }
    }
  };

  /**
   * Create a element with same css styles and content with watermark and check if all the styles match the actual one.
   * If not, push modified property into `changedStyleList`
   * @param {CSSStyleDeclaration} computedStyle
   * @param {[string]} changedStyleList
   */
  validateWithClonedElement = async (computedStyle, changedStyleList) => {
    const { username } = this.props;
    const watermarkElement = this.watermarkElement.current;
    const applyStyle = this.getStyle();

    const validationElement = document.createElement('div');
    validationElement.innerHTML = username;
    for (const styleKey in applyStyle) {
      validationElement.style[styleKey] = applyStyle[styleKey];
    }
    watermarkElement.parentElement.appendChild(validationElement);

    const validationStyle = window.getComputedStyle(validationElement);
    for (let i = 0; i < validationStyle.length; i++) {
      const key = validationStyle.item(i);
      if (['top', 'right', 'bottom', 'left'].indexOf(key) !== -1) continue;
      if (computedStyle[key] !== validationStyle[key]) {
        changedStyleList.push(`${key}:${computedStyle[key]}`);
      }
    }

    validationElement.remove();
  };

  /**
   * Track how many point are painted on a blank canvas to see if user is using an invisible font
   * @param {CSSStyleDeclaration} computedStyle
   */
  validateFontWithCanvas = async computedStyle => {
    const computedStyleFont = computedStyle.font; // capture font first, value will be different after any `await`
    const width = 512;
    const height = 16;
    try {
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'black';
      ctx.fillRect(0, 0, width, height);
      ctx.font = computedStyle.font;
      ctx.fillStyle = 'white';
      ctx.textBaseline = 'top';
      ctx.fillText('abcdefghijklmnopqrstuvwxyz0123456789._', 0, 0); // All possible characters of username
      const imageData = ctx.getImageData(0, 0, width, height);
      let filledPixelCount = 0;
      for (let i = 0; i < imageData.data.length; i += 4) {
        const r = imageData.data[i]; // red portion of the pixel
        if (r > 0) {
          filledPixelCount++;
        }
      }
      if (this.trackedValidationPixelCounts?.indexOf(filledPixelCount) === -1) {
        await this.trackWatermarkValidation({
          filledPixelCount,
          computedStyleFont,
        });
        this.trackedValidationPixelCounts.push(filledPixelCount);
        await setItem('_twvpc', this.trackedValidationPixelCounts);
      }
    } catch (error) {
      const errorString = error.toString();
      if (this.trackedValidationPixelCounts?.indexOf(errorString) === -1) {
        await this.trackWatermarkValidation({
          computedStyleFont,
          error: errorString,
        });
        this.trackedValidationPixelCounts.push(errorString);
        await setItem('_twvpc', this.trackedValidationPixelCounts);
      }
    }
  };

  /**
   * Track intersection area of actual video and watermark
   * @param {VideoBounds} videoBounds
   * @param {HTMLElement} watermarkElement
   * @param {import('video.js').VideoJsPlayer} player
   */
  validateWithBounds = (videoBounds, watermarkElement, player) => {
    const videoElement = player.el().querySelector('video');
    const videoClientRect = videoElement.getBoundingClientRect();

    const videoContentX1 =
      videoClientRect.x + (videoClientRect.width - videoBounds.realWidth) / 2;
    const videoContentY1 =
      videoClientRect.y + (videoClientRect.height - videoBounds.realHeight) / 2;
    const videoContentX2 = videoContentX1 + videoBounds.realWidth;
    const videoContentY2 = videoContentY1 + videoBounds.realHeight;

    const watermarkRect = watermarkElement.getBoundingClientRect();
    const watermarkX1 = watermarkRect.x;
    const watermarkY1 = watermarkRect.y;
    const watermarkX2 = watermarkRect.x + watermarkRect.width;
    const watermarkY2 = watermarkRect.y + watermarkRect.height;

    const intersectWidth = Math.max(
      0,
      Math.min(videoContentX2, watermarkX2) -
        Math.max(videoContentX1, watermarkRect.x)
    );
    const intersectHeight = Math.max(
      0,
      Math.min(videoContentY2, watermarkY2) -
        Math.max(videoContentY1, watermarkRect.y)
    );
    const watermarkArea = watermarkRect.width * watermarkRect.height;
    const intersectArea = intersectWidth * intersectHeight;

    if (intersectArea < watermarkArea) {
      const report = {
        videoX1: videoClientRect.x,
        videoY1: videoClientRect.y,
        videoX2: videoClientRect.x + videoClientRect.width,
        videoY2: videoClientRect.y + videoClientRect.height,
        videoContentX1,
        videoContentY1,
        videoContentX2,
        videoContentY2,
        watermarkX1,
        watermarkY1,
        watermarkX2,
        watermarkY2,
        watermarkArea,
        videoArea: videoClientRect.width * videoClientRect.height,
        videoContentArea: videoBounds.realWidth * videoBounds.realHeight,
        intersectArea,
      };
      trackMixpanel({
        type: EventTypes.USER_WATERMARK_INTERSECION_VALIDATE,
        payload: {
          viewId: getViewId({
            pathname: window.location.pathname,
          }),
          messageId: this.props.messageId,
          userUsername: this.props.userUsername,
          report,
        },
      });
    }
  };

  updateWatermark = () => {
    // TODO: Image stories are not checked
    if (!this.triggerTimeoutHandle && !this.isTriggerPostponed) {
      this.validateWatermark();
    }

    if (this.isWindowSizeDirty) {
      this.videoBounds = this.calculateVideoBounds(this.props.player);
      this.isWindowSizeDirty = false;
    }
    const { minLeft, maxLeft, minTop, maxTop } = this.videoBounds;

    const left = Math.round(Math.random() * (maxLeft - minLeft) + minLeft);
    const top = Math.round(Math.random() * (maxTop - minTop) + minTop);

    this.setState({
      watermarkTop: top,
      watermarkLeft: left,
      watermarkKey: Date.now(),
    });
  };

  /**
   * Calculate the bounds of video inside the player
   * @param {import('video.js').VideoJsPlayer} player
   */
  calculateVideoBounds(player) {
    const { realWidth, realHeight } = getPlayerRealSize({ player });
    const verticalMargin = (player.currentHeight() - realHeight) / 2;
    const horizontalMargin = (player.currentWidth() - realWidth) / 2;
    const minLeft = Math.round(horizontalMargin);
    const maxLeft = Math.round(
      Math.max(
        minLeft,
        horizontalMargin + realWidth - this.watermarkElement.current.clientWidth
      )
    );
    const minTop = Math.round(verticalMargin);
    const maxTop = Math.round(
      Math.max(
        minTop,
        verticalMargin + realHeight - this.watermarkElement.current.clientHeight
      )
    );
    return {
      realWidth,
      realHeight,
      minLeft,
      maxLeft,
      minTop,
      maxTop,
    };
  }

  render() {
    return (
      <div
        ref={this.watermarkElement}
        key={this.state.watermarkKey}
        className="notranslate" // Prevent Google Translate from mutating the dom
        style={this.getStyle()}
      >
        {this.props.username}
      </div>
    );
  }
}

VideoWatermark.propTypes = {
  isSuspended: PropTypes.bool.isRequired,
  player: PropTypes.object,
  username: PropTypes.string,
  userUsername: PropTypes.string,
  messageId: PropTypes.string,
  startWatermarkFlag: PropTypes.number,
};

VideoWatermark.defaultProps = {
  player: {},
  username: '',
  userUsername: '',
  messageId: '',
  startWatermarkFlag: null,
};

export default VideoWatermark;
